import axios from "axios";
import { reportDenial } from "../../rbac/access";

// project open in the app: sent as X-Project-Id so the backend checks the user's role on THIS project
let currentProjectId: string | null = null;
export const setBoqProject = (id: any) => { currentProjectId = id != null && String(id) !== "" && !isNaN(Number(id)) ? String(id) : null; };
/** the session's access token (the BOQ API verifies it with the auth service) */
const sessionToken = (): string => {
  try { return JSON.parse(localStorage.getItem("access-cookie") || "{}")?.access_token || ""; } catch { return ""; }
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/gateWay/bim";

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach stored token as Authorization header for authenticated BIM requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access-cookie");
  if (token) {
    config.headers["Authorization"] = token;
  }
  if (currentProjectId && !config.headers["X-Project-Id"]) config.headers["X-Project-Id"] = currentProjectId;
  return config;
});

// Handle 401 — redirect to login; auto-retry transient network errors (no response).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("access-cookie");
      localStorage.removeItem("currentUser");
      window.location.href = "/";
      return Promise.reject(error);
    }
    // refused by project RBAC: explain it and offer "Request access" (the caller still gets the error)
    // (opening a project without access shows a full page instead of the dialog)
    if (error.response?.status === 403 && !(error.response?.data?.code === "NO_PROJECT_ACCESS" && String(error.config?.method).toLowerCase() === "get")) reportDenial(error.response?.data);
    // Retry on a true network error (no HTTP response) for idempotent GETs — covers
    // transient connection blips that otherwise surface as "Network Error" in the UI.
    const cfg: any = error.config || {};
    const isNetworkErr = !error.response && (error.code === "ERR_NETWORK" || error.message === "Network Error");
    const method = (cfg.method || "get").toLowerCase();
    if (isNetworkErr && method === "get" && (cfg.__retryCount || 0) < 2) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      await new Promise((r) => setTimeout(r, 400 * cfg.__retryCount));
      return api(cfg);
    }
    return Promise.reject(error);
  }
);

// ============================================================
// BOQ Agent API (agentic 2D -> BOQ microservice, api/boq/*)
// The agentic controller runs on the bim-core-boq service (CORS enabled),
// which is NOT behind the /gateWay/bim gateway. So we hit it directly via a
// dedicated axios instance. Base URL + API key are env-configurable, so this
// can be pointed at a gateway path in production without touching call sites.
// ============================================================
const BOQ_API_BASE = process.env.REACT_APP_BOQ_API_BASE_URL || "http://localhost:52011";
const BOQ_API_KEY = process.env.REACT_APP_BOQ_API_KEY || "procurex-boq-key-2026";

export const boqHttp = axios.create({ baseURL: BOQ_API_BASE, withCredentials: false });
boqHttp.interceptors.response.use(
  (r) => {
    // Some endpoints (RBAC-guarded review/resolve, fill/correct) return {error:'forbidden…'}
    // at HTTP 200 instead of throwing — surface it as a real error so the UI reacts.
    const err = r?.data?.error ?? r?.data?.data?.error ?? r?.data?.response?.error;
    if (err) return Promise.reject({ response: { data: { message: err }, status: 403 } });
    return r;
  },
  (error) => {
    const code = error.response?.data?.code;
    if (error.response?.status === 401 && code === "LOGIN_REQUIRED") {
      // the session behind the token has ended (signed out / expired): back to sign-in
      localStorage.removeItem("access-cookie");
      localStorage.removeItem("currentUser");
      window.location.href = "/";
    } else if (error.response?.status === 401) {
      // eslint-disable-next-line no-console
      console.warn("BOQ agent API unauthorized — check REACT_APP_BOQ_API_KEY");
    }
    if (error.response?.status === 403) reportDenial(error.response?.data);
    return Promise.reject(error);
  }
);

const BOQ_DEFAULT_ORG = process.env.REACT_APP_BOQ_DEFAULT_ORG || "1";

// Active project's org — set by ProjectDetail from the loaded project. This is the
// most reliable org source for direct :52011 calls, because the JWT often has no org
// claim (currentUser.orgId can be "") and a project may belong to a different tenant.
let boqOrgOverride: string | null = null;
export const setBoqOrg = (org: any) => {
  const s = org === 0 || org ? String(org).trim() : "";
  boqOrgOverride = s && s !== "0" && !isNaN(Number(s)) ? s : null;
};

/**
 * Build auth headers for the agentic backend.
 * The guard REQUIRES a non-empty x-org-id in API-key mode, and browsers drop
 * empty-valued headers — so we must always emit a valid numeric org.
 * Priority: active project's org > currentUser.orgId > default.
 */
export const boqCtx = () => {
  let orgId = BOQ_DEFAULT_ORG;
  try {
    const u = JSON.parse(localStorage.getItem("currentUser") || "{}");
    const cand = [u?.orgId, u?.orgid, u?.org_id, u?.organizationId, u?.org?.id]
      .map((x: any) => (x === 0 || x ? String(x).trim() : ""))
      .find((x: string) => x && x !== "0" && !isNaN(Number(x)));
    if (cand) orgId = cand;
  } catch {
    /* fall back to defaults */
  }
  if (boqOrgOverride) orgId = boqOrgOverride; // project org wins (the server replaces it with the project's verified org)
  // who is calling: the session token (verified server-side) - permissions come from the user's role on the project
  const headers: Record<string, string> = { "X-API-Key": BOQ_API_KEY, "X-Org-Id": orgId };
  const token = sessionToken();
  if (token) headers["x-user-token"] = token;
  if (currentProjectId) headers["X-Project-Id"] = currentProjectId;
  return { headers };
};

/** Audit trail (org admins / audit.view) and problems seen in the browser */
export const auditApi = {
  list: (q: Record<string, any>) => boqHttp.get("/api/boq/audit/list", { ...boqCtx(), params: q }),
  summary: (q: Record<string, any>) => boqHttp.get("/api/boq/audit/summary", { ...boqCtx(), params: q }),
  /** best-effort: never throws, never blocks the caller */
  clientEvent: (e: { action: string; projectId?: number | null; entityType?: string; entityId?: string | number; error?: string; detail?: any }) => {
    try { boqHttp.post("/api/boq/audit/client-event", { ...e, page: window.location.pathname }, boqCtx()).catch(() => undefined); } catch { /* ignore */ }
  },
};

/** RBAC: my permissions (org + project) and "request access" for a refused action */
export const rbacApi = {
  me: (projectId?: number | null) => boqHttp.get("/api/boq/rbac/me", { ...boqCtx(), params: projectId ? { projectId } : {} }),
  requestAccess: (body: { projectId?: number; permission: string; action?: string }) => boqHttp.post("/api/boq/rbac/request-access", body, boqCtx()),
  // discipline scope of the organisation's roles (null = every discipline)
  roleDisciplines: () => boqHttp.get("/api/boq/rbac/roles/disciplines", boqCtx()),
  setRoleDisciplines: (roleId: number, disciplines: string[] | null) => boqHttp.post("/api/boq/rbac/roles/disciplines", { roleId, disciplines }, boqCtx()),
};

export const boqAgentApi = {
  // --- Stage 0: project ---
  createProject: (body: { name: string; projectType?: string; country?: string; client?: string; location?: string }) =>
    boqHttp.post("/api/boq/project/create", body, boqCtx()),

  // --- Stage 1: upload / convert ---
  upload: (projectId: number, file: File, opts?: { sheetRole?: string; discipline?: string; tower?: string; floors?: string[]; scope?: string; onUploadProgress?: (e: any) => void }) => {
    const fd = new FormData();
    fd.append("projectId", String(projectId));
    fd.append("file", file);
    if (opts?.sheetRole) fd.append("sheetRole", opts.sheetRole);   // tag register/schedule so Validate auto-finds it
    if (opts?.discipline) fd.append("discipline", opts.discipline); // for folder grouping
    if (opts?.tower) fd.append("tower", opts.tower);                 // building chosen in the upload settings
    if (opts?.floors?.length) fd.append("floors", JSON.stringify(opts.floors)); // floors it covers (multiplier = count)
    if (opts?.scope) fd.append("scope", opts.scope);
    return boqHttp.post("/api/boq/upload", fd, { ...boqCtx(), headers: { ...boqCtx().headers, "Content-Type": "multipart/form-data" }, onUploadProgress: opts?.onUploadProgress });
  },
  reconvert: (projectId: number) => boqHttp.post("/api/boq/reconvert", { projectId }, boqCtx()),
  // list drawings from :52011 (current code) so per-file `tower` survives a refresh
  listDwgs: (projectId: number) => boqHttp.post("/api/boq/dwg/list", { projectId }, boqCtx()),
  // update a sheet's metadata (discipline / QTO role / tower / floor list) — org-consistent with uploads.
  // `floors` (e.g. ["2","4","6","8"]) drives the multiplier server-side (= floors.length).
  updateSheet: (body: { dwgId: number; discipline?: string; sheetRole?: string; sheetUse?: string | null; scope?: string; floorMultiplier?: number; tower?: string | null; floors?: string[]; keepSuggested?: boolean }) =>
    boqHttp.post("/api/boq/sheet/update", body, boqCtx()),
  // project structure (tower-based / single + tower list + floors-per-tower)
  getStructure: (projectId: number) => boqHttp.post("/api/boq/project/structure", { projectId }, boqCtx()),
  // full project configuration (layout, buildings / types, unit counts, levels, variants)
  getConfiguration: (projectId: number) => boqHttp.post("/api/boq/project/configuration", { projectId }, boqCtx()),
  setConfiguration: (projectId: number, configuration: any) => boqHttp.post("/api/boq/project/configuration/set", { projectId, configuration }, boqCtx()),
  configurationAi: (projectId: number, prompt: string, current?: any) => boqHttp.post("/api/boq/project/configuration/ai", { projectId, prompt, current }, { ...boqCtx(), timeout: 120000 }),
  // per building / floor: drawings, take-off lines, BOQ lines (diagram hover)
  // viewer details panel: drawing facts, take-off lines measured from it, its issues
  dwgSummary: (dwgId: number) => boqHttp.post("/api/boq/dwg/summary", { dwgId }, boqCtx()),
  /** every take-off line's recorded citation positions on one drawing (viewer context layer, read only) */
  dwgMarks: (dwgId: number) => boqHttp.post("/api/boq/dwg/marks", { dwgId }, boqCtx()),
  getConfigurationStats: (projectId: number) => boqHttp.post("/api/boq/project/configuration/stats", { projectId }, boqCtx()),
  setStructure: (body: { projectId: number; structureType: string; towers: string[]; towerFloors?: Record<string, string[]>; unitCounts?: Record<string, number> }) =>
    boqHttp.post("/api/boq/project/structure/set", body, boqCtx()),

  // --- Stage 2: validate / completeness ---
  reconcile: (projectId: number) => boqHttp.post("/api/boq/reconcile", { projectId }, boqCtx()),
  reconcileRegister: (projectId: number, file: File) => {
    const fd = new FormData();
    fd.append("projectId", String(projectId));
    fd.append("file", file);
    return boqHttp.post("/api/boq/reconcile-register", fd, { ...boqCtx(), headers: { ...boqCtx().headers, "Content-Type": "multipart/form-data" } });
  },
  requirements: (projectId: number) => boqHttp.post("/api/boq/requirements", { projectId }, boqCtx()),
  requestList: (projectId: number) => boqHttp.post("/api/boq/request-list", { projectId }, boqCtx()),
  // reconcile using the register already uploaded on the Upload screen (no re-upload)
  reconcileRegisterStored: (projectId: number) => boqHttp.post("/api/boq/reconcile-register-stored", { projectId }, boqCtx()),

  // --- Stage 3: classify / confirm ---
  validateFloors: (projectId: number) => boqHttp.post("/api/boq/takeoff/validate-floors", { projectId }, boqCtx()),
  // re-classify: re-read current file tags, raise questions only for unconfirmed files (never re-ask),
  // returns { questionsRaised, openQuestions, changedFiles, qtoStale, qtoRun }
  // Confirm & Classify readiness check (drawings vs project configuration), fixes, project rules
  classifyReadiness: (projectId: number) => boqHttp.post("/api/boq/classify/readiness", { projectId }, boqCtx()),
  // drawing checks (Confirm & Classify): external references, PDF scale, units / position / rotation
  // drawing package readiness (DWG Files > Readiness) and QTO reconciliation
  packageReadiness: (projectId: number) => boqHttp.post("/api/boq/package/readiness", { projectId }, { ...boqCtx(), timeout: 180000 }),
  dashboardOverview: (body: { projectId?: number | null; discipline?: string | null; building?: string | null; days?: number }) =>
    boqHttp.post("/api/boq/dashboard/overview", body, { ...boqCtx(), timeout: 300000 }),
  qtoReconcile: (projectId: number) => boqHttp.post("/api/boq/qto/reconcile", { projectId }, { ...boqCtx(), timeout: 180000 }),
  qtoReconcileReference: (projectId: number, fileName: string, rows: any[]) => boqHttp.post("/api/boq/qto/reconcile/reference", { projectId, fileName, rows }, boqCtx()),
  qtoReconcileDecide: (body: { projectId: number; key: string; decision: string; cause?: string; note?: string }) => boqHttp.post("/api/boq/qto/reconcile/decide", body, boqCtx()),
  // project code register: type codes / equipment tags / unmatched symbols with their meaning from the project's legends + schedules
  codeRegisterList: (projectId: number, f: { discipline?: string; system?: string; status?: string; kind?: string } = {}) => boqHttp.post("/api/boq/code-register/list", { projectId, ...f }, boqCtx()),
  codeRegisterBuild: (projectId: number) => boqHttp.post("/api/boq/code-register/build", { projectId }, { ...boqCtx(), timeout: 180000 }),
  codeRegisterUpdate: (body: { id: number; status?: string; meaning?: string | null; scheduleMode?: string; packageMode?: string; billScope?: string | null; comment?: string | null }) => boqHttp.post("/api/boq/code-register/update", body, boqCtx()),
  codeRegisterResolve: (body: { id: number; choice: "a" | "b" | "custom"; value?: string | null; comment?: string | null }) => boqHttp.post("/api/boq/code-register/resolve", body, boqCtx()),
  // BOQ mapping: the bound template's sections, where each of our item types goes, held lines, the QS's format settings
  boqFormatView: (projectId: number) => boqHttp.post("/api/boq/boq-format/view", { projectId }, boqCtx()),
  boqFormatSave: (projectId: number, body: { sectionMap?: Record<string, string | null>; finishes?: string; itemFormat?: string | null; scope?: any; collapseCircuits?: boolean; circuitRef?: string | null; holdZ?: number | null; comment?: string }) =>
    boqHttp.post("/api/boq/boq-format/save", { projectId, ...body }, boqCtx()),
  // trade & element library (org versions, project pin + overrides) and the allowance register (provisional sums as money)
  boqLibraryView: (projectId: number) => boqHttp.post("/api/boq/boq-library/view", { projectId }, boqCtx()),
  boqLibraryDraft: (projectId: number, entries: any[], note?: string) => boqHttp.post("/api/boq/boq-library/draft", { projectId, entries, note }, boqCtx()),
  boqLibraryApprove: (projectId: number) => boqHttp.post("/api/boq/boq-library/approve", { projectId }, boqCtx()),
  boqLibraryPin: (projectId: number, version: number) => boqHttp.post("/api/boq/boq-library/pin", { projectId, version }, boqCtx()),
  boqLibraryOverride: (projectId: number, body: { ruleId: string; field: string; value?: any; scope?: string; reason?: string; approve?: boolean; remove?: boolean }) =>
    boqHttp.post("/api/boq/boq-library/override", { projectId, ...body }, boqCtx()),
  boqAllowances: (projectId: number) => boqHttp.post("/api/boq/boq-allowance/list", { projectId }, boqCtx()),
  boqAllowanceSave: (projectId: number, body: any) => boqHttp.post("/api/boq/boq-allowance/save", { projectId, ...body }, boqCtx()),
  boqAllowanceDelete: (projectId: number, id: number) => boqHttp.post("/api/boq/boq-allowance/delete", { projectId, id }, boqCtx()),
  drawingChecks: (projectId: number) => boqHttp.post("/api/boq/drawing-checks/status", { projectId }, boqCtx()),
  drawingChecksRun: (projectId: number, recheck = false) => boqHttp.post("/api/boq/drawing-checks/run", { projectId, recheck }, boqCtx()),
  drawingChecksAck: (body: { projectId: number; dwgId: number; check: "scale" | "xref" | "units"; value?: any; note?: string; remove?: boolean }) => boqHttp.post("/api/boq/drawing-checks/ack", body, boqCtx()),
  roomsList: (projectId: number, removed = false) => boqHttp.post("/api/boq/rooms/list", { projectId, removed }, boqCtx()),
  // a room on the drawings (same shape as a QTO line's citation), its history, and the QS edits (all recorded with history)
  roomsCitation: (projectId: number, id: number) => boqHttp.post("/api/boq/rooms/citation", { projectId, id }, boqCtx()),
  roomsHistory: (projectId: number, id: number) => boqHttp.post("/api/boq/rooms/history", { projectId, id }, boqCtx()),
  roomsEdit: (projectId: number, body: { id: number; roomType?: string; level?: string; name?: string; area?: number | null; comment?: string }) => boqHttp.post("/api/boq/rooms/edit", { projectId, ...body }, boqCtx()),
  roomsAdd: (projectId: number, body: { name: string; roomType: string; tower?: string; level?: string; area: number; comment?: string }) => boqHttp.post("/api/boq/rooms/add", { projectId, ...body }, boqCtx()),
  roomsRemove: (projectId: number, ids: number[], comment?: string) => boqHttp.post("/api/boq/rooms/remove", { projectId, ids, comment }, boqCtx()),
  roomsRestore: (projectId: number, ids: number[], comment?: string) => boqHttp.post("/api/boq/rooms/restore", { projectId, ids, comment }, boqCtx()),
  roomsDetect: (projectId: number, recheck = false, dwgIds?: number[]) => boqHttp.post("/api/boq/rooms/detect", { projectId, recheck, dwgIds }, boqCtx()),
  roomsUpdate: (projectId: number, ids: number[], body: { roomType?: string; confirm?: boolean }) => boqHttp.post("/api/boq/rooms/update", { projectId, ids, ...body }, boqCtx()),
  roomsMergeTypes: (projectId: number, from: string[], to: string) => boqHttp.post("/api/boq/rooms/merge-types", { projectId, from, to }, boqCtx()),
  classifySuggestStart: (projectId: number, readViews = false) => boqHttp.post("/api/boq/classify/suggest/start", { projectId, readViews }, boqCtx()),
  classifySuggestStatus: (projectId: number) => boqHttp.post("/api/boq/classify/suggest/status", { projectId }, boqCtx()),
  classifyApply: (projectId: number, body: { changes?: any[]; confirm?: number[]; remember?: { kind: string; token: string; value: string }[] }) => boqHttp.post("/api/boq/classify/apply", { projectId, ...body }, boqCtx()),
  classifyAddBuilding: (projectId: number, name: string) => boqHttp.post("/api/boq/classify/add-building", { projectId, name }, boqCtx()),
  classifyRuleDelete: (projectId: number, id: number) => boqHttp.post("/api/boq/classify/rule/delete", { projectId, id }, boqCtx()),
  reclassify: (projectId: number) => boqHttp.post("/api/boq/classify/reclassify", { projectId }, boqCtx()),

  // --- Stage 4: link / citations ---
  citationStats: (projectId: number) => boqHttp.post("/api/boq/citation/stats", { projectId }, boqCtx()),
  resolveCitations: (projectId: number) => boqHttp.post("/api/boq/takeoff/resolve-citations", { projectId }, { ...boqCtx(), timeout: 600000 }),
  citationLine: (body: { projectId: number; qtoLineId?: number; fillId?: number }) => boqHttp.post("/api/boq/citation/line", body, boqCtx()),
  // 3D: resolve IFC GlobalId → APS dbId for every 3D citation (uses already-translated models).
  resolveCitations3d: (projectId: number) => boqHttp.post("/api/boq/qto/resolve-citations-3d", { projectId }, { ...boqCtx(), timeout: 600000 }),
  // 3D: on-demand citation for one element (per-element QTO grid → isolate it in the viewer).
  elementCitation: (body: { projectId: number; elementId: number }) => boqHttp.post("/api/boq/qto/element-citation", body, { ...boqCtx(), timeout: 120000 }),
  // Last saved AI-Quality (model readiness) results — restores the panel after a page refresh.
  savedQuality: (projectId: number, modelId?: number) => boqHttp.post("/api/boq/quality/saved", { projectId, modelId }, boqCtx()),
  // Recover materials from APS (type/Revit-property materials our parser missed) → element_materials.
  syncMaterialsFromAps: (projectId: number) => boqHttp.post("/api/boq/qto/sync-materials-aps", { projectId }, { ...boqCtx(), timeout: 600000 }),
  // Floors — "filter by floor" support for QTO + BOQ.
  projectFloors: (projectId: number) => boqHttp.post("/api/boq/qto/floors", { projectId }, boqCtx()),
  elementFloors: (projectId: number) => boqHttp.post("/api/boq/qto/element-floors", { projectId }, { ...boqCtx(), timeout: 120000 }),
  apsViewerToken: () => boqHttp.post("/api/boq/aps/viewer-token", {}, boqCtx()),
  // prepare one drawing for the APS Viewer (DWG → translate → urn; PDF → direct). Idempotent — poll it.
  apsPrepare: (dwgId: number) => boqHttp.post("/api/boq/aps/prepare", { dwgId }, boqCtx()),

  // --- Stage 5: QTO / take-off ---
  // scope (optional): { disciplines, dwgIds, force } - only those drawings are measured, the rest reuse saved results
  runTakeoff: (projectId: number, scope?: { disciplines?: string[]; dwgIds?: number[]; force?: boolean } | null) =>
    boqHttp.post("/api/boq/takeoff/run", { projectId, ...(scope ? { scope } : {}) }, { ...boqCtx(), timeout: 600000 }),
  runTakeoffSync: (projectId: number) => boqHttp.post("/api/boq/takeoff/run-sync", { projectId }, { ...boqCtx(), timeout: 600000 }),
  takeoffJob: (jobId: number | string) => boqHttp.post("/api/boq/takeoff/job", { jobId }, boqCtx()),        // poll one job's progress
  takeoffJobs: (projectId: number) => boqHttp.post("/api/boq/takeoff/jobs", { projectId }, boqCtx()),       // list jobs (resume active on refresh)
  qtoOverride: (body: { id: number; quantity: number; reason?: string }) => boqHttp.post("/api/boq/qto/override", body, boqCtx()),  // edit a measured line qty
  // QS measurement standards (thickness + rebar ratios) — blank = engine default; user value wins
  getStandards: (projectId: number) => boqHttp.post("/api/boq/project/standards", { projectId }, boqCtx()),
  setStandards: (projectId: number, standards: any, sources?: Record<string, any>) => boqHttp.post("/api/boq/project/standards/set", { projectId, standards, ...(sources && Object.keys(sources).length ? { sources } : {}) }, boqCtx()),
  // AI auto-fill; async returns a job id to poll (takeoffJob) - optionally for one building or building type
  suggestStandards: (projectId: number, opts?: { async?: boolean; building?: string; category?: string }) => boqHttp.post("/api/boq/project/standards/suggest", { projectId, ...(opts || {}) }, boqCtx()),
  qtoDerivation: (qtoLineId: number) => boqHttp.post("/api/boq/qto/derivation", { qtoLineId }, boqCtx()),
  /** the line's structured Basis / Formula (read-only, always fresh incl. edits + marks) */
  qtoBasisDetail: (qtoLineId: number) => boqHttp.post("/api/boq/qto/basis-detail", { qtoLineId }, boqCtx()),
  qtoCitation: (qtoLineId: number, family?: boolean) => boqHttp.post("/api/boq/qto/citation", { qtoLineId, family }, boqCtx()),
  // QS edit of a line (quantity / unit, comment required; kept on re-runs) and the line's change history
  qtoLineEdit: (body: { id: number; quantity?: number | null; uom?: string | null; comment?: string; reset?: boolean; expectedVersion?: number | null }) => boqHttp.post("/api/boq/qto/line/edit", body, boqCtx()),
  qtoLineHistory: (id: number) => boqHttp.post("/api/boq/qto/line/history", { id }, boqCtx()),
  // calculation core (21A): formula register, calculation policy, rules, benchmarks, assumptions, check runs, line records
  calc: (path: string, projectId: number, body: any = {}, timeout?: number) => boqHttp.post(`/api/boq/calc/${path}`, { projectId, ...body }, timeout ? { ...boqCtx(), timeout } : boqCtx()),
  // QTO line actions (as on BOQ lines): Line details (wording / bill / unit / building + comment), Remove line (reason),
  // removed lines + Restore, New line (manual) - all kept on take-off re-runs
  qtoLineDetails: (body: { id: number; description?: string; bill?: string; uom?: string; building?: string; comment: string }) => boqHttp.post("/api/boq/qto/line/details", body, boqCtx()),
  qtoLineDelete: (id: number, comment: string) => boqHttp.post("/api/boq/qto/line/delete", { id, comment }, boqCtx()),
  qtoLineRemoved: (projectId: number) => boqHttp.post("/api/boq/qto/line/removed", { projectId }, boqCtx()),
  qtoLineRestore: (projectId: number, historyId: number, comment?: string) => boqHttp.post("/api/boq/qto/line/restore", { projectId, historyId, comment }, boqCtx()),
  qtoLineCreate: (projectId: number, body: { building?: string; bill: string; description: string; uom: string; quantity: number; comment: string; sourceDwgId?: number | null }) =>
    boqHttp.post("/api/boq/qto/line/create", { projectId, ...body }, boqCtx()),
  // typical-floor exceptions: a line's floor-by-floor table; override / exclude one floor (reason required); remove
  qtoFloors: (id: number) => boqHttp.post("/api/boq/qto/line-floors", { id }, boqCtx()),
  qtoFloorSet: (body: { id: number; dwgId: number; floor: string; action: "override" | "exclude"; quantity?: number | null; reason: string }) => boqHttp.post("/api/boq/qto/line-floors/set", body, boqCtx()),
  qtoFloorRemove: (body: { id: number; exceptionId: number; reason?: string }) => boqHttp.post("/api/boq/qto/line-floors/remove", body, boqCtx()),
  // measurement rules (versioned profile): read / save as a new draft / approve; impact preview before saving;
  // project summary (unresolved / inclusions / lines per rule); schedule vs plan discrepancies; a line's calculation record
  measurementProfile: (projectId: number, version?: number) => boqHttp.post("/api/boq/measurement-profile", { projectId, ...(version ? { version } : {}) }, boqCtx()),
  measurementProfileSave: (projectId: number, rules: any, note: string, expectedVersion: number) => boqHttp.post("/api/boq/measurement-profile/save", { projectId, rules, note, expectedVersion }, boqCtx()),
  measurementProfileApprove: (projectId: number, version: number) => boqHttp.post("/api/boq/measurement-profile/approve", { projectId, version }, boqCtx()),
  measurementProfilePreview: (projectId: number, rules: any) => boqHttp.post("/api/boq/measurement-profile/preview", { projectId, rules }, boqCtx()),
  measurementSummary: (projectId: number) => boqHttp.post("/api/boq/measurement-profile/summary", { projectId }, boqCtx()),
  qtoDiscrepancies: (projectId: number) => boqHttp.post("/api/boq/qto/discrepancies", { projectId }, boqCtx()),
  qtoDiscrepancyReview: (projectId: number, id: number, decision: string, note?: string, value?: string) => boqHttp.post("/api/boq/qto/discrepancies/review", { projectId, id, decision, note, value }, boqCtx()),
  qtoLineCalc: (lineId: number) => boqHttp.post("/api/boq/qto/line-calc", { lineId }, boqCtx()),
  // typical-floor repetition: multi-floor sheets and their confirmation (lines from unconfirmed sheets are held)
  floorConfirmList: (projectId: number) => boqHttp.post("/api/boq/qto/floor-confirm", { projectId }, boqCtx()),
  floorConfirmSet: (projectId: number, dwgIds: number[], confirm: boolean, bulk?: { all?: boolean; building?: string }) => boqHttp.post("/api/boq/qto/floor-confirm/set", { projectId, dwgIds, confirm, ...(bulk || {}) }, boqCtx()),
  // AI QA review; ids = only these queue items; async returns a job id to poll (takeoffJob)
  qaAutoResolve: (projectId: number, opts?: { async?: boolean; ids?: number[] }) => boqHttp.post("/api/boq/qa/auto-resolve", { projectId, ...(opts || {}) }, boqCtx()),
  templateCurrent: (projectId: number) => boqHttp.post("/api/boq/template/current", { projectId }, boqCtx()),
  templateLines: (projectId: number) => boqHttp.post("/api/boq/template/lines", { projectId }, boqCtx()),
  projectMeta: (projectId: number) => boqHttp.post("/api/boq/project/meta", { projectId }, boqCtx()),
  qtoResults: (projectId: number) => boqHttp.post("/api/boq/qto/results", { projectId }, boqCtx()),
  // take-off lines per discipline (left menu under QTO) and what a run with a scope would measure
  qtoDisciplineCounts: (projectId: number) => boqHttp.post("/api/boq/qto/discipline-counts", { projectId }, boqCtx()),
  takeoffPlan: (projectId: number, scope: any) => boqHttp.post("/api/boq/takeoff/plan", { projectId, scope }, boqCtx()),
  visionCount: (body: any) => boqHttp.post("/api/boq/vision/count", body, boqCtx()),

  // agent questions + QA review
  agentQuestions: (projectId: number) => boqHttp.post("/api/boq/agent/questions", { projectId }, boqCtx()),
  // backend answerQuestion reads body.id (the question id) + body.answer + body.skip
  agentAnswer: (body: { projectId: number; questionId: number | string; answer: any; skip?: boolean }) =>
    boqHttp.post("/api/boq/agent/answer", { projectId: body.projectId, id: body.questionId, answer: body.answer, skip: body.skip }, boqCtx()),
  reviewQueue: (projectId: number) => boqHttp.post("/api/boq/review/queue", { projectId }, boqCtx()),
  // backend resolveReviewItem reads body.id + body.status + body.correctedQty (requires x-role qs/admin)
  reviewResolve: (body: { projectId: number; id: number | string; status?: string; correctedQty?: number }) =>
    boqHttp.post("/api/boq/review/resolve", { projectId: body.projectId, id: body.id, status: body.status, correctedQty: body.correctedQty }, boqCtx()),

  // --- Stage 6: BOQ / template fill ---
  // Materialize a 3D/IFC project's take-off into the agentic QTO table (no-op for 2D).
  qtoFromModel: (projectId: number) => boqHttp.post("/api/boq/qto/from-model", { projectId }, { ...boqCtx(), timeout: 300000 }),
  // What BOQ breakdown the 3D model supports (tower/floor/common) — for the UI label.
  qtoModelStructure: (projectId: number) => boqHttp.post("/api/boq/qto/model-structure", { projectId }, boqCtx()),
  // generate the BOQ; async:true returns a job id with live progress (takeoffJob) on servers that support it
  fill: (projectId: number, opts?: { async?: boolean }) => boqHttp.post("/api/boq/fill", { projectId, ...(opts || {}) }, { ...boqCtx(), timeout: 600000 }),
  fillGet: (projectId: number, tower?: string) => boqHttp.post("/api/boq/fill/get", { projectId, tower }, boqCtx()),
  // backend correctFill(fillId, qtoLineId) — LINKS a template line to a measured QTO line
  // (requires x-role qs/admin). It does NOT accept an arbitrary qty/status.
  fillCorrect: (body: { projectId: number; fillId: number | string; qtoLineId: number }) =>
    boqHttp.post("/api/boq/fill/correct", { projectId: body.projectId, fillId: body.fillId, qtoLineId: body.qtoLineId }, boqCtx()),
  // QS manual override: set a template line's qty directly (qs/admin only)
  fillOverride: (body: { projectId: number; fillId: number | string; qty: number; status?: string }) =>
    boqHttp.post("/api/boq/fill/override", { projectId: body.projectId, fillId: body.fillId, qty: body.qty, status: body.status }, boqCtx()),
  fillCitation: (body: { projectId: number; fillId: number }) => boqHttp.post("/api/boq/fill/citation", body, boqCtx()),
  // project rate library
  getRates: (projectId: number) => boqHttp.post("/api/boq/rates/get", { projectId }, boqCtx()),
  setRates: (projectId: number, items: any[]) => boqHttp.post("/api/boq/rates/set", { projectId, items }, boqCtx()),
  applyRates: (projectId: number) => boqHttp.post("/api/boq/rates/apply", { projectId }, boqCtx()),
  deleteFillLine: (projectId: number, id: number | string, comment?: string) => boqHttp.post("/api/boq/fill/delete", { projectId, id, comment }, boqCtx()),
  // every change to a BOQ line (who / what / when / why) + the QTO quantity changes of its take-off line
  fillHistory: (projectId: number, id: number | string) => boqHttp.post("/api/boq/fill/history", { projectId, id }, boqCtx()),
  // QS edit of a BOQ line's quantity / unit (comment required; kept when the BOQ is generated again); reset = back to measured
  fillLineEdit: (projectId: number, body: { id: number | string; quantity?: number | null; uom?: string | null; comment?: string; reset?: boolean }) =>
    boqHttp.post("/api/boq/fill/line/edit", { projectId, ...body }, boqCtx()),
  // take-off lines the last Generate did not bill, each with its reason
  fillLeftOut: (projectId: number) => boqHttp.post("/api/boq/fill/left-out", { projectId }, boqCtx()),
  // project audit trail: every change in the project - who, when, what (before -> after), why
  projectAudit: (projectId: number, f: { category?: string; user?: string; from?: string; to?: string; search?: string; outcome?: string; page?: number; pageSize?: number } = {}) =>
    boqHttp.post("/api/boq/project/audit", { projectId, ...f }, boqCtx()),
  // manual BOQ line CRUD — Amount derives from qty×rate; a rate here also updates the rate library
  createFillLine: (projectId: number, body: { tower?: string; bill?: string; description?: string; unit?: string; qty?: number | null; rate?: number | null; comment?: string }) =>
    boqHttp.post("/api/boq/fill/create", { projectId, ...body }, boqCtx()),
  editFillLine: (projectId: number, body: { id: number | string; bill?: string; description?: string; unit?: string; qty?: number | null; rate?: number | null; comment?: string }) =>
    boqHttp.post("/api/boq/fill/edit", { projectId, ...body }, boqCtx()),
  // rate library row delete — clears the rate everywhere (library + matching BOQ lines)
  clearRate: (projectId: number, body: { bill?: string; description?: string; unit?: string }) =>
    boqHttp.post("/api/boq/rates/clear", { projectId, ...body }, boqCtx()),
  // --- quantity basis (measured / derived / allowance / unresolved), QS review decisions, release gate ---
  qtoBasisSummary: (projectId: number) => boqHttp.post("/api/boq/qto/basis/summary", { projectId }, boqCtx()),
  qtoBasisRefresh: (projectId: number) => boqHttp.post("/api/boq/qto/basis/refresh", { projectId }, boqCtx()),
  // decision: accept | correct | reject | request_info | approve_allowance (| exclude | exception); expectedVersion = the line's rowVersion
  qtoReviewDecide: (projectId: number, body: { qtoLineId: number; decision: string; quantity?: number | null; reason?: string; rule?: string; expectedVersion?: number | null; scope?: "line" | "rule" }) =>
    boqHttp.post("/api/boq/qto/review/decide", { projectId, ...body }, boqCtx()),
  // wall decisions: every wall line's function / construction (from the drawings or a QS decision), the Wall decision form,
  // save (draft | clarification | complete), withdraw, the clarification requests to the designer
  qtoWallLines: (projectId: number) => boqHttp.post("/api/boq/qto/wall/lines", { projectId }, boqCtx()),
  qtoWallGet: (projectId: number, qtoLineId: number) => boqHttp.post("/api/boq/qto/wall/get", { projectId, qtoLineId }, boqCtx()),
  qtoWallSave: (projectId: number, body: { qtoLineId: number; data: any; action: "draft" | "clarification" | "complete"; expectedVersion?: number | null }) =>
    boqHttp.post("/api/boq/qto/wall/save", { projectId, ...body }, { ...boqCtx(), timeout: 120000 }),
  qtoWallWithdraw: (projectId: number, qtoLineId: number, reason?: string) => boqHttp.post("/api/boq/qto/wall/withdraw", { projectId, qtoLineId, reason }, boqCtx()),
  qtoWallClarifications: (projectId: number) => boqHttp.post("/api/boq/qto/wall/clarifications", { projectId }, boqCtx()),
  qtoReviewDecisions: (projectId: number, qtoLineId?: number) => boqHttp.post("/api/boq/qto/review/decisions", { projectId, qtoLineId }, boqCtx()),
  // cable metre lines: the feeders of a line, a QS feeder length (measured / schedule, with evidence), withdrawing one
  qtoCableFeeders: (projectId: number, qtoLineId: number) => boqHttp.post("/api/boq/qto/cable/feeders", { projectId, qtoLineId }, boqCtx()),
  /** a pipe line's drawn segments by size, with every other size of the same system (pipe sizes along the network) */
  qtoPipeSegments: (projectId: number, qtoLineId: number) => boqHttp.post("/api/boq/qto/pipe/segments", { projectId, qtoLineId }, boqCtx()),
  // drawn lengths (cable feeders / pipe runs drawn on a sheet): overlay of a sheet, what drawing needs, save (pending), QS approve / reject, withdraw
  /** cable lengths v3 (feeder register, calculation versions, approval, buckets) - one route family; see cableV3Api.ts */
  cableV3: (path: string, body: Record<string, any>) => boqHttp.post(`/api/boq/qto/cable-v3/${path}`, body, boqCtx()),
  drawnOverlay: (projectId: number, dwgId: number) => boqHttp.post("/api/boq/qto/drawn/overlay", { projectId, dwgId }, boqCtx()),
  drawnContext: (projectId: number, qtoLineId: number, feederKey?: string | null) => boqHttp.post("/api/boq/qto/drawn/context", { projectId, qtoLineId, feederKey: feederKey || undefined }, boqCtx()),
  drawnList: (projectId: number, q: { qtoLineId?: number; dwgId?: number; status?: string; feederKey?: string }) => boqHttp.post("/api/boq/qto/drawn/list", { projectId, ...q }, boqCtx()),
  drawnSave: (projectId: number, body: { qtoLineId: number; feederKey?: string; dwgId: number; points: number[][]; verticalM?: number | null; verticalBasis?: string | null; sizeMm?: number | null; reason?: string; expectedRowVersion?: number; replaceId?: number; expectedVersion?: number }) => boqHttp.post("/api/boq/qto/drawn/save", { projectId, ...body }, boqCtx()),
  drawnApprove: (projectId: number, id: number, version: number, note?: string) => boqHttp.post("/api/boq/qto/drawn/approve", { projectId, id, version, note }, boqCtx()),
  drawnReject: (projectId: number, id: number, version: number, note: string) => boqHttp.post("/api/boq/qto/drawn/reject", { projectId, id, version, note }, boqCtx()),
  drawnWithdraw: (projectId: number, id: number, reason?: string) => boqHttp.post("/api/boq/qto/drawn/withdraw", { projectId, id, reason }, boqCtx()),
  /** the QS gives a size to one piece of a "size not shown" run (applied at once) / undoes it */
  drawnSize: (projectId: number, b: { qtoLineId: number; dwgId: number; segs: { a: number[]; b: number[] }[]; sizeMm: number; note?: string }) => boqHttp.post("/api/boq/qto/drawn/size", { projectId, ...b }, boqCtx()),
  drawnSizeUndo: (projectId: number, id: number, reason?: string) => boqHttp.post("/api/boq/qto/drawn/size/undo", { projectId, id, reason }, boqCtx()),
  drawnSizes: (projectId: number, b: { qtoLineId?: number; dwgId?: number }) => boqHttp.post("/api/boq/qto/drawn/sizes", { projectId, ...b }, boqCtx()),
  qtoCableFeederLength: (projectId: number, body: { qtoLineId: number; feederKey: string; totalM?: number | null; horizontalM?: number | null; verticalM?: number | null; includesVertical?: boolean; includesAllowance?: boolean; sourceKind?: "qs-measured" | "schedule"; evidence?: { dwgId?: number | null; name?: string | null; handle?: string | null; note?: string | null } | null; reason?: string; expectedVersion?: number | null }) =>
    boqHttp.post("/api/boq/qto/cable/feeder-length", { projectId, ...body }, boqCtx()),
  qtoCableFeederMap: (projectId: number, body: { qtoLineId: number; feederKey: string; to: string; kind?: "board" | "equipment"; reason?: string; expectedVersion?: any }) => boqHttp.post("/api/boq/qto/cable/feeder-map", { projectId, ...body }, boqCtx()),
  qtoCableFeederUnmap: (projectId: number, body: { qtoLineId: number; feederKey: string; reason?: string }) => boqHttp.post("/api/boq/qto/cable/feeder-map/clear", { projectId, ...body }, boqCtx()),
  qtoCableRiser: (projectId: number, body: { tower: string; tags?: string; points?: string }) => boqHttp.post("/api/boq/qto/cable/riser", { projectId, ...body }, boqCtx()),
  qtoCableFeederClear: (projectId: number, body: { qtoLineId: number; feederKey: string; reason?: string }) => boqHttp.post("/api/boq/qto/cable/feeder-length/clear", { projectId, ...body }, boqCtx()),
  // cable lengths v2: length basis (per cable / total, per sheet or feeder), inferred connections, riser per layout, reconciliation
  qtoCableLengthBasis: (projectId: number, body: { qtoLineId: number; feederKey: string; scope: "sheet" | "feeder"; basis: "per-run" | "total" | null; reason?: string }) => boqHttp.post("/api/boq/qto/cable/length-basis", { projectId, ...body }, boqCtx()),
  qtoCableApproveConnections: (projectId: number, body: { qtoLineId: number; feederKeys?: string[]; all?: boolean; reason?: string; withdraw?: boolean }) => boqHttp.post("/api/boq/qto/cable/connections/approve", { projectId, ...body }, boqCtx()),
  qtoCableRiserAt: (projectId: number, body: { tower: string; dwgId: number; at: string | null; evidence?: string | null }) => boqHttp.post("/api/boq/qto/cable/riser/layout", { projectId, ...body }, boqCtx()),
  qtoCableInputs: (projectId: number) => boqHttp.post("/api/boq/qto/cable/inputs", { projectId }, boqCtx()),
  qtoCableFloorHeight: (projectId: number, body: { floor: string; heightM: number; reason?: string }) => boqHttp.post("/api/boq/qto/cable/floor-height", { projectId, ...body }, boqCtx()),
  qtoCableQsSave: (projectId: number, rows: { feeder: string; expectedM: number; source?: string; runs?: number | null; note?: string }[]) => boqHttp.post("/api/boq/qto/cable/qs-check/save", { projectId, rows }, boqCtx()),
  qtoCableReconcile: (projectId: number, versionId?: number | null) => boqHttp.post("/api/boq/qto/cable/reconcile", { projectId, versionId: versionId ?? null }, boqCtx()),
  qtoReviewStaleCheck: (projectId: number) => boqHttp.post("/api/boq/qto/review/stale-check", { projectId }, boqCtx()),
  qtoReviewRevoke: (projectId: number, decisionId: number) => boqHttp.post("/api/boq/qto/review/revoke", { projectId, decisionId }, boqCtx()),
  releaseCheck: (projectId: number, versionId: number) => boqHttp.post("/api/boq/release/check", { projectId, versionId }, boqCtx()),
  // before exporting a version: lines needing revalidation / held / not traceable since the snapshot (guide S4.8)
  releaseExportCheck: (projectId: number, versionId: number) => boqHttp.post("/api/boq/release/export-check", { projectId, versionId }, boqCtx()),
  // what a proposed standards / drawing / rule change would affect - read only (guide S5.9 / S6.12)
  qtoReviewImpact: (projectId: number, body: { standards?: Record<string, number | string | null>; tower?: string | null; files?: number[]; rules?: string[] }) =>
    boqHttp.post("/api/boq/qto/review/impact", { projectId, ...body }, boqCtx()),
  // findings (guide §25): rule / severity / owner / OPEN-ASSIGNED-RESOLVED-WAIVED / evidence
  qtoFindings: (projectId: number, body: { qtoLineId?: number; status?: string } = {}) => boqHttp.post("/api/boq/qto/finding/list", { projectId, ...body }, boqCtx()),
  qtoFindingAssign: (projectId: number, id: number, owner: string) => boqHttp.post("/api/boq/qto/finding/assign", { projectId, id, owner }, boqCtx()),
  qtoFindingResolve: (projectId: number, id: number, evidence: string) => boqHttp.post("/api/boq/qto/finding/resolve", { projectId, id, evidence }, boqCtx()),
  qtoFindingWaive: (projectId: number, id: number, reason: string) => boqHttp.post("/api/boq/qto/finding/waive", { projectId, id, reason }, boqCtx()),
  releaseException: (projectId: number, body: { keys: string[]; decision: "exclude" | "exception"; reason: string; versionId?: number }) =>
    boqHttp.post("/api/boq/release/exception", { projectId, ...body }, boqCtx()),
  // --- source register · regions · location allocation · export manifest (guide §8/§9/§13, CFG01-03, CFG11, CFG13) ---
  sourceRegisterList: (projectId: number) => boqHttp.post("/api/boq/source-register/list", { projectId }, boqCtx()),
  sourceRegisterRefresh: (projectId: number, opts?: { checksums?: boolean; probePdfs?: boolean }) => boqHttp.post("/api/boq/source-register/refresh", { projectId, ...(opts || {}) }, boqCtx()),
  sourceRegisterRevision: (projectId: number, body: { dwgId: number; drawingNo: string; revision?: string | null; issueStatus?: string | null; title?: string | null; system?: string | null }) =>
    boqHttp.post("/api/boq/source-register/revision", { projectId, ...body }, boqCtx()),
  sourceRegisterUseFor: (projectId: number, dwgId: number, useFor: string) => boqHttp.post("/api/boq/source-register/use-for", { projectId, dwgId, useFor }, boqCtx()),
  regionsList: (projectId: number, dwgId: number) => boqHttp.post("/api/boq/source-register/regions", { projectId, dwgId }, boqCtx()),
  regionsRead: (projectId: number, dwgId: number, recheck = false) => boqHttp.post("/api/boq/source-register/regions/read", { projectId, dwgId, recheck }, boqCtx()),
  /** read the views / legends / title block of every drawing not read yet (background; progress in sourceRegisterList().regionsJob) */
  /** confirm every titled view and legend (roles as proposed) of the drawings named - all drawings when dwgIds is empty */
  regionsConfirmTitled: (projectId: number, dwgIds: number[] = []) => boqHttp.post("/api/boq/source-register/regions/confirm-titled", { projectId, dwgIds }, boqCtx()),
  regionsReadAll: (projectId: number, recheck = false) => boqHttp.post("/api/boq/source-register/regions/read-all", { projectId, recheck }, boqCtx()),
  regionsConfirm: (projectId: number, body: { dwgId: number; key: string; role: string; counted?: boolean }) => boqHttp.post("/api/boq/source-register/regions/confirm", { projectId, ...body }, boqCtx()),
  regionsAdd: (projectId: number, body: { dwgId: number; role: string; bbox: number[]; title?: string; counted?: boolean }) => boqHttp.post("/api/boq/source-register/regions/add", { projectId, ...body }, boqCtx()),
  locationSetting: (projectId: number) => boqHttp.post("/api/boq/location/setting", { projectId }, boqCtx()),
  locationSettingSave: (projectId: number, body: { datumLevel: string | null; datumElevation?: number | null; atDatum: string; unlocatedProposal: string; boundaryTerms?: string[]; approve?: boolean }) =>
    boqHttp.post("/api/boq/location/setting/save", { projectId, ...body }, boqCtx()),
  locationLines: (projectId: number) => boqHttp.post("/api/boq/location/lines", { projectId }, boqCtx()),
  locationShares: (projectId: number, body: { lineKey: string; shares: { below: number; above: number; atGrade?: number }; reason?: string; approve?: boolean }) =>
    boqHttp.post("/api/boq/location/shares", { projectId, ...body }, boqCtx()),
  locationSharesClear: (projectId: number, lineKey: string) => boqHttp.post("/api/boq/location/shares/clear", { projectId, lineKey }, boqCtx()),
  sourceImpact: (projectId: number) => boqHttp.post("/api/boq/source-register/impact", { projectId }, boqCtx()),
  lineSources: (projectId: number, qtoLineId: number) => boqHttp.post("/api/boq/source-register/line-sources", { projectId, qtoLineId }, boqCtx()),
  boqVersionManifest: (projectId: number, versionId: number) => boqHttp.post("/api/boq/version/manifest", { projectId, versionId }, boqCtx()),
  // --- BOQ versioning · approval · compare (2D flow, mirrors 3D POMI review) ---
  boqVersionCreate: (projectId: number, note?: string) => boqHttp.post("/api/boq/version/create", { projectId, note }, boqCtx()),
  boqVersionList: (projectId: number) => boqHttp.post("/api/boq/version/list", { projectId }, boqCtx()),
  boqVersionGet: (projectId: number, versionId: number) => boqHttp.post("/api/boq/version/get", { projectId, versionId }, boqCtx()),
  boqVersionApprove: (projectId: number, versionId: number) => boqHttp.post("/api/boq/version/approve", { projectId, versionId }, boqCtx()),
  boqVersionCompare: (projectId: number, versionA: number, versionB: number) =>
    boqHttp.post("/api/boq/version/compare", { projectId, versionA, versionB }, boqCtx()),
  // --- Viewer-anchored issue tracking (2D + 3D) ---
  issueCreate: (projectId: number, body: any) => boqHttp.post("/api/boq/issue/create", { projectId, ...body }, boqCtx()),
  issueList: (projectId: number) => boqHttp.post("/api/boq/issue/list", { projectId }, boqCtx()),
  usersList: () => boqHttp.post("/api/boq/users/list", {}, boqCtx()),   // RBAC org users for the Assign-to dropdown
  issueGet: (projectId: number, id: number) => boqHttp.post("/api/boq/issue/get", { projectId, id }, boqCtx()),
  issueUpdate: (projectId: number, id: number, patch: any) => boqHttp.post("/api/boq/issue/update", { projectId, id, ...patch }, boqCtx()),
  issueComment: (projectId: number, id: number, body: string, author?: string) => boqHttp.post("/api/boq/issue/comment", { projectId, id, body, author }, boqCtx()),
  issueDelete: (projectId: number, id: number) => boqHttp.post("/api/boq/issue/delete", { projectId, id }, boqCtx()),

  // --- Email a BOQ report (FE builds the xlsx, backend attaches + sends) ---
  emailReport: (payload: { projectId: number; projectName?: string; tower?: string; files: { fileName: string; fileBase64: string }[]; recipients: string[]; message?: string; senderName?: string }) =>
    boqHttp.post("/api/boq/report/email", payload, boqCtx()),

  // --- Stage 7: coverage / report / export ---
  coverage: (projectId: number) => boqHttp.post("/api/boq/coverage", { projectId }, boqCtx()),
  gap: (projectId: number) => boqHttp.post("/api/boq/gap", { projectId }, boqCtx()),
  report: (projectId: number) => boqHttp.post("/api/boq/report", { projectId }, boqCtx()),

  // --- templates ---
  templateList: () => boqHttp.post("/api/boq/template/list", {}, boqCtx()),
  templateSet: (body: any) => boqHttp.post("/api/boq/template/set", body, boqCtx()),
  /** the org's bill templates (id, name, version, default) - for pickers; no project needed */
  templateOrgList: () => boqHttp.post("/api/boq/template/org-list", {}, boqCtx()),
  templateListBrief: (projectId: number) => boqHttp.post("/api/boq/template/list", { projectId, brief: true }, boqCtx()),
};

// ============================================================
// BIM Model API
// ============================================================
export const bimModelApi = {
  uploadModel: (formData: FormData) =>
    api.post("/model/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  listModels: (params: {
    projectId: number;
    pageSize?: number;
    sortOrder?: string;
    direction?: string;
  }) =>
    api.get("/model/list", { params }),

  getModel: (modelId: number) =>
    api.get(`/model/id/${modelId}`),

  deleteModel: (modelId: number) =>
    api.delete(`/model/delete/${modelId}`),

  getVersions: (modelId: number) =>
    api.get(`/model/versions/${modelId}`),

  compareVersions: (data: {
    modelId: number;
    versionAId: number;
    versionBId: number;
  }) =>
    api.post("/model/compare-versions", data),

  /** Returns the gateway proxy URL for downloading an IFC file (bypasses Azure CORS) */
  getDownloadProxyUrl: (modelId: number) =>
    `${API_BASE}/model/download/${modelId}`,

  /** Download IFC file as ArrayBuffer via gateway proxy (bypasses Azure CORS) */
  downloadModelFile: (modelId: number) =>
    api.get(`/model/download/${modelId}`, { responseType: "arraybuffer" }),
};

// ============================================================
// BIM Viewer API
// ============================================================
export const bimViewerApi = {
  getTiles: (modelId: number) =>
    api.get(`/viewer/tiles/${modelId}`),

  getThumbnail: (modelId: number) =>
    api.get(`/viewer/thumbnail/${modelId}`),

  getSpatialTree: (modelId: number) =>
    api.get(`/viewer/spatial-tree/${modelId}`),

  getAnnotations: (modelId: number) =>
    api.get(`/viewer/annotation/list/${modelId}`),

  createAnnotation: (data: any) =>
    api.post("/viewer/annotation/create", data),

  deleteAnnotation: (annotationId: number) =>
    api.delete(`/viewer/annotation/delete/${annotationId}`),

  generateTiles: (modelId: number, lodLevel?: number) =>
    api.post('/viewer/tiles/generate', { modelId, lodLevel: lodLevel ?? 0 }),

  getTileStatus: (modelId: number) =>
    api.get(`/viewer/tiles/status/${modelId}`),

  /** Returns the gateway proxy URL for downloading a tile GLB */
  getTileDownloadUrl: (modelId: number, tileIndex: number) =>
    `${API_BASE}/viewer/tiles/download/${modelId}/${tileIndex}`,

  getViewStates: (modelId: number) =>
    api.get(`/viewer/view-states/${modelId}`),

  saveViewState: (data: any) =>
    api.post("/viewer/view-states", data),

  deleteViewState: (viewStateId: number) =>
    api.delete(`/viewer/view-states/${viewStateId}`),
};

// ============================================================
// APS (Autodesk Platform Services) — fast SVF2 3D viewer (additive)
// ============================================================
export const bimApsApi = {
  /** Short-lived viewables:read token for the Autodesk Viewer SDK. */
  getToken: () => api.get("/viewer/aps/token"),
  /** Live SVF2 translation status + urn for a model. */
  getStatus: (modelId: number) => api.get(`/viewer/aps/status/${modelId}`),
  /** Upload the model to APS and start SVF2 translation. */
  translate: (modelId: number) => api.post(`/viewer/aps/translate/${modelId}`, {}),
};

// ============================================================
// BIM Parser API
// ============================================================
export const bimParserApi = {
  getSpatialStructure: (modelId: number) =>
    api.get(`/parser/spatialStructure/${modelId}`),

  getElement: (elementId: number) =>
    api.get(`/parser/elements/id/${elementId}`),

  getElementProperties: (elementId: number) =>
    api.get(`/parser/elements/properties/${elementId}`),

  getElementMaterials: (elementId: number) =>
    api.get(`/parser/elements/materials/${elementId}`),

  getElementsByClass: (modelId: number, ifcClass: string) =>
    api.get(`/parser/elementsByClass`, { params: { modelId, ifcClass } }),

  getClassificationSummary: (modelId: number) =>
    api.get(`/parser/classificationSummary/${modelId}`),

  triggerParse: (modelId: number, forceReparse = false) =>
    api.post(`/parser/trigger`, { modelId, forceReparse }),
};

// ============================================================
// BIM Project API
// ============================================================
export const bimProjectApi = {
  listProjects: () =>
    api.get("/project/list"),

  getProject: (projectId: string) =>
    api.get(`/project/id/${projectId}`),

  getProjectDashboard: (projectId: string) =>
    api.get(`/project/dashboard/${projectId}`),

  createProject: (data: any) =>
    api.post("/project/create", data),

  updateProject: (projectId: string, data: any) =>
    api.put(`/project/update/${projectId}`, data),
  updateSettings: (data: any) =>
    api.put(`/project/settings/update`, data),

  archiveProject: (projectId: string) =>
    api.put(`/project/archive/${projectId}`),
};

// ============================================================
// BIM DWG API (Phase 1 — 2D pipeline)
// ============================================================
export interface DwgUploadMeta {
  discipline?: string;
  sheetNumber?: string;
  sheetColor?: string;
  description?: string;
  sheetRole?: string;
  floorMultiplier?: number;
}

export const bimDwgApi = {
  uploadDwg: (
    projectId: number,
    file: File,
    meta?: DwgUploadMeta,
    onUploadProgress?: (e: any) => void,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", String(projectId));
    if (meta?.discipline) formData.append("discipline", meta.discipline);
    if (meta?.sheetNumber) formData.append("sheetNumber", meta.sheetNumber);
    if (meta?.sheetColor) formData.append("sheetColor", meta.sheetColor);
    if (meta?.description) formData.append("description", meta.description);
    if (meta?.sheetRole) formData.append("sheetRole", meta.sheetRole);
    if (meta?.floorMultiplier) formData.append("floorMultiplier", String(meta.floorMultiplier));
    return api.post("/dwg/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    });
  },

  listDwgs: (projectId: number) =>
    api.get(`/dwg/list/${projectId}`),

  getStatus: (dwgId: number) =>
    api.get(`/dwg/status/${dwgId}`),

  getDownloadUrl: (dwgId: number) =>
    api.get(`/dwg/download/${dwgId}`),

  getDxfUrl: (dwgId: number) =>
    api.get(`/dwg/dxf/${dwgId}`),

  getLayers: (dwgId: number) =>
    api.get(`/dwg/layers/${dwgId}`),

  deleteDwg: (dwgId: number) =>
    api.delete(`/dwg/delete/${dwgId}`),

  updateSheet: (data: { dwgId: number; sheetRole?: string; floorMultiplier?: number; discipline?: string }) =>
    api.post("/dwg/sheet/update", data),

  // ---- 2D Layer Classification ----
  classifyLayers: (projectId: number) =>
    api.post(`/dwg/classify/${projectId}`, {}),

  getClassification: (projectId: number) =>
    api.get(`/dwg/classification/${projectId}`),

  updateLayerClassification: (data: {
    layerId: number; pomiSection: string; subTrade?: string;
    elementType?: string; uom?: string; qtyKind?: string;
  }) => api.post("/dwg/classification/update", data),

  confirmLayers: (layerIds: number[]) =>
    api.post("/dwg/classification/confirm", { layerIds }),

  saveLayerAsOrgRule: (layerId: number) =>
    api.post("/dwg/classification/save-as-rule", { layerId }),

  applyAiClassifications: (items: Array<{
    layerId: number; pomiSection: string; subTrade?: string;
    elementType?: string; uom?: string; qtyKind?: string; confidence?: string;
  }>) => api.post("/dwg/classification/apply-ai", { items }),

  // ---- 2D QTO ----
  runQto: (projectId: number) =>
    api.post(`/dwg/qto/run/${projectId}`, {}),
  getQtoCoverage: (projectId: number) =>
    api.get(`/dwg/qto/coverage/${projectId}`),
  getQtoResults: (projectId: number) =>
    api.get(`/dwg/qto/results/${projectId}`),
  overrideQto: (data: { id: number; quantity: number; reason?: string }) =>
    api.post("/dwg/qto/override", data),
  confirmQto: (projectId: number) =>
    api.post("/dwg/qto/confirm", { projectId }),

  // ---- 2D BOQ ----
  generateBoq: (projectId: number) =>
    api.post(`/dwg/boq/generate/${projectId}`, {}),
  getBoq: (projectId: number) =>
    api.get(`/dwg/boq/${projectId}`),
  updateBoqRate: (data: { id: number; rate: number }) =>
    api.post("/dwg/boq/rate", data),

  // ---- 2D viewer state: Saved Views ----
  saveView: (data: {
    dwgId: number; name: string;
    camMinX?: number; camMinY?: number; camMaxX?: number; camMaxY?: number;
    hiddenLayers?: string[]; bgDark?: boolean;
  }) => api.post("/dwg/views/save", data),
  listViews: (dwgId: number) => api.get(`/dwg/views/${dwgId}`),
  deleteView: (id: number) => api.delete(`/dwg/views/${id}`),

  // ---- 2D viewer state: Markups (pins + measurements) ----
  saveMarkup: (data: {
    dwgId: number; kind: "pin" | "measure"; label?: string;
    anchorX?: number; anchorY?: number;
    measureKind?: string; measureValue?: number; measureUom?: string;
    points?: Array<{ x: number; y: number }>; issueId?: number;
  }) => api.post("/dwg/markups/save", data),
  listMarkups: (dwgId: number) => api.get(`/dwg/markups/${dwgId}`),
  deleteMarkup: (id: number) => api.delete(`/dwg/markups/${id}`),
};

// ============================================================
// BIM Clash API
// ============================================================
export const bimClashApi = {
  getClashResults: (params: { projectId?: string; modelId?: number; severity?: string }) =>
    api.get("/clash/results", { params }),

  getClashSummary: (projectId: number | string) =>
    api.get(`/clash/summary/${projectId}`),

  resolveClash: (clashId: number | string) =>
    api.patch(`/clash/resolve/${clashId}`),

  ignoreClash: (clashId: number | string) =>
    api.patch(`/clash/ignore/${clashId}`),

  triggerClashDetection: (data: {
    projectId: number;
    sourceModelId?: number;
    targetModelId?: number;
    testName?: string;
    tolerance?: number;
    clashType?: string;
    modelIds?: number[];
    detectionMethod?: string;
  }) => api.post("/clash/trigger", data),

  getClashProgress: (testId: number | string) =>
    api.get(`/clash/progress/${testId}`),

  getClashGroups: (params: { clashTestId: number | string }) =>
    api.get("/clash/groups", { params }),

  runClashGrouping: (data: { clashTestId: number; enableAi?: boolean }) =>
    api.post("/clash/group", data),

  updateGroupStatus: (groupId: number | string, data: { status: string; assignedTo?: number }) =>
    api.patch(`/clash/groups/${groupId}/status`, data),
};

// ============================================================
// BIM User Management API
// ============================================================
export const bimUserApi = {
  listUsers: (params?: { search?: string }) =>
    api.get("/clash/users", { params }),
  createUser: (data: { fullName: string; email: string; phone?: string; department?: string; designation?: string }) =>
    api.post("/clash/users/create", data),
  deleteUser: (userId: number) =>
    api.delete(`/clash/users/${userId}`),
  getAssignments: () =>
    api.get("/clash/users/assignments"),
};

// ============================================================
// BIM BCF & Report API
// ============================================================
export const bimBcfApi = {
  exportBcf: (projectId: number | string) =>
    api.get(`/clash/bcf/export/${projectId}`),

  importBcf: (data: any) =>
    api.post("/clash/bcf/import", data),

  generateReport: (data: { clashTestId: number; format?: string }) =>
    api.post("/clash/report/generate", data),
};

// ============================================================
// BIM Coordination Issues API
// ============================================================
export const bimIssuesApi = {
  createIssue: (data: any) =>
    api.post("/clash/issues/create", data),

  listIssues: (params: { projectId: number | string; status?: string; priority?: string }) =>
    api.get("/clash/issues", { params }),

  getIssue: (issueId: number | string) =>
    api.get(`/clash/issues/${issueId}`),

  updateIssueStatus: (issueId: number | string, data: { status?: string; assignedTo?: number; assignedToName?: string; priority?: string }) =>
    api.patch(`/clash/issues/${issueId}/status`, data),

  addIssueComment: (issueId: number | string, data: { content: string }) =>
    api.post(`/clash/issues/${issueId}/comment`, data),
};

// ============================================================
// BIM QTO API
// ============================================================
export const bimQtoApi = {
  triggerQto: (data: { modelId: number; projectId?: number; forceRecalculate?: boolean }) =>
    api.post("/qto/trigger", data),

  getQtoStatus: (modelId: number) =>
    api.get(`/qto/status/${modelId}`),

  getQtoResults: (modelId: number) =>
    api.get(`/qto/results/${modelId}`),

  getQtoSummary: (modelId: number) =>
    api.get(`/qto/summary/${modelId}`),

  getQtoByElement: (elementId: number) =>
    api.get(`/qto/element/${elementId}`),

  getQtoByStorey: (params: { modelId: number; storeyId?: number }) =>
    api.get("/qto/byStorey", { params }),

  getQtoByClass: (params: { modelId: number; ifcClass?: string }) =>
    api.get("/qto/byClass", { params }),

  getQtoRules: (params?: { discipline?: string }) =>
    api.get("/qto/rules", { params }),

  createQtoRule: (data: any) =>
    api.post("/qto/rules/create", data),

  updateQtoRules: (data: any) =>
    api.put("/qto/rules/update", data),

  updateQtoRule: (data: any) =>
    api.put("/qto/rules/update", data),

  deleteQtoRule: (ruleId: number) =>
    api.delete(`/qto/rules/delete/${ruleId}`),

  updateQtoOverride: (data: any) =>
    api.put("/qto/override/update", data),

  resetQtoOverride: (qtoResultId: number) =>
    api.patch(`/qto/override/reset/${qtoResultId}`),

  getQtoHistory: (modelId: number, projectId?: number) =>
    api.get(`/qto/history/${modelId}`, { params: projectId ? { projectId } : {} }),
};

// ============================================================
// BIM BOQ API
// ============================================================
export const bimBoqApi = {
  generateBoq: (data: { projectId: number; modelId?: number; templateId?: number }) =>
    api.post("/boq/generate", data),

  listBoq: (params: { projectId?: string }) =>
    api.get("/boq/list", { params }),

  getOneBoq: (boqId: number) =>
    api.get(`/boq/id/${boqId}`),

  getBoqSections: (boqId: number) =>
    api.get(`/boq/sections/${boqId}`),

  getBoqItems: (boqId: number) =>
    api.get(`/boq/items/${boqId}`),

  updateBoqItem: (data: any) =>
    api.put("/boq/items/update", data),

  deleteBoqItem: (itemId: number) =>
    api.delete(`/boq/items/delete/${itemId}`),

  addBoqItem: (data: any) =>
    api.post("/boq/items/add", data),

  getBoqSummary: (boqId: number) =>
    api.get(`/boq/summary/${boqId}`),

  approveBoq: (boqId: number) =>
    api.patch(`/boq/approve/${boqId}`),

  rejectBoq: (boqId: number, data?: { reason?: string }) =>
    api.patch(`/boq/reject/${boqId}`, data),

  submitForApproval: (boqId: number) =>
    api.patch(`/boq/submitForApproval/${boqId}`),

  compareVersions: (data: { projectId: number; versionA: number; versionB: number }) =>
    api.post("/boq/compare", data),

  getVariance: (comparisonId: number) =>
    api.get(`/boq/variance/${comparisonId}`),

  getBoqHistory: (projectId: number) =>
    api.get(`/boq/history/${projectId}`),
};

// ============================================================
// BIM BOQ Engine API (Zero-Config BOQ Generator)
// ============================================================
export const bimBoqEngineApi = {
  generateBoqEngine: (data: { projectId: number; modelId: number; force?: boolean }) =>
    api.post("/boq-engine/generate", data),
  boqVersions: (projectId: number) =>
    api.post(`/boq-engine/action`, { action: "boq.versions", payload: { projectId } }),
  boqConsolidated: (projectId: number, resultIds?: number[]) =>
    api.post(`/boq-engine/action`, { action: "boq.consolidated", payload: { projectId, resultIds } }),
  boqCompareVersions: (projectId: number, versionA: number, versionB: number) =>
    api.post(`/boq-engine/action`, { action: "boq.compareVersions", payload: { projectId, versionA, versionB } }),
  getBoqEngineStatus: (projectId: number, modelId: number) =>
    api.get(`/boq-engine/status/${projectId}/${modelId}`),
  getBoqEngineResult: (projectId: number, modelId: number) =>
    api.get(`/boq-engine/result/${projectId}/${modelId}`),
  updateBoqEngineRate: (data: { lineItemId: number; unitRate: number }) =>
    api.put("/boq-engine/rate/update", data),
  exportBoqEngine: (projectId: number, modelId: number, format: "csv" | "xlsx" | "pdf" | "gaeb" = "csv", view: "full" | "material" | "labour" | "equipment" = "full") =>
    api.get(`/boq-engine/export/${projectId}/${modelId}`, { params: { format, view }, responseType: "blob" }),
  getBoqEngineHistory: (projectId: number, modelId: number) =>
    api.get(`/boq-engine/history/${projectId}/${modelId}`),
  getClassificationDrilldown: (boqResultId: number, sectionCode?: string) =>
    api.get(`/boq-engine/drilldown/${boqResultId}`, { params: { sectionCode } }),
  // Production actions (readiness / snapshots / approval / audit / templates / compare / packs)
  action: (action: string, payload: any) =>
    api.post(`/boq-engine/action`, { action, payload }),
  // Hybrid classification: deterministic pass → gaps → approve gap → masters
  classifyRun: (projectId: number, modelId: number) =>
    api.post(`/boq-engine/action`, { action: "classify.run", payload: { projectId, modelId } }),
  classifyGaps: (projectId: number, modelId: number) =>
    api.post(`/boq-engine/action`, { action: "classify.gaps", payload: { projectId, modelId } }),
  classifyApprove: (payload: { projectId: number; ifcClass: string; category: string; boqSection: string; csiCode?: string; source?: string }) =>
    api.post(`/boq-engine/action`, { action: "classify.approve", payload }),
  classifySummary: (projectId: number) =>
    api.post(`/boq-engine/action`, { action: "classify.summary", payload: { projectId } }),
  classifyList: (projectId: number, source?: string, section?: string, page = 1, pageSize = 25) =>
    api.post(`/boq-engine/action`, { action: "classify.list", payload: { projectId, source, section, page, pageSize } }),
  classifySuggest: (ifcClass: string, samples: string[]) =>
    api.post(`/boq-engine/action`, { action: "classify.suggest", payload: { ifcClass, samples } }),
  classifyHistory: (projectId: number) =>
    api.post(`/boq-engine/action`, { action: "classify.history", payload: { projectId } }),
  pipelineStatus: (projectId: number) =>
    api.post(`/boq-engine/action`, { action: "pipeline.status", payload: { projectId } }),
  checkReadiness: (projectId: number, modelId: number) =>
    api.post(`/boq-engine/action`, { action: "readiness.check", payload: { projectId, modelId } }),
  createSnapshot: (projectId: number, modelId: number, name: string) =>
    api.post(`/boq-engine/action`, { action: "snapshot.create", payload: { projectId, modelId, name } }),
  listSnapshots: (projectId: number, modelId: number) =>
    api.post(`/boq-engine/action`, { action: "snapshot.list", payload: { projectId, modelId } }),
  compareResults: (aId: number, bId: number) =>
    api.post(`/boq-engine/action`, { action: "compare", payload: { aId, bId } }),
  setApproval: (resultId: number, status: string, note?: string) =>
    api.post(`/boq-engine/action`, { action: "approval.set", payload: { resultId, status, note } }),
  listActivity: (projectId: number, modelId: number) =>
    api.post(`/boq-engine/action`, { action: "activity.list", payload: { projectId, modelId } }),
  saveTemplate: (projectId: number, modelId: number, name: string, standard?: string) =>
    api.post(`/boq-engine/action`, { action: "template.save", payload: { projectId, modelId, name, standard } }),
  listTemplates: () =>
    api.post(`/boq-engine/action`, { action: "template.list", payload: {} }),
  getPack: (country: string) =>
    api.post(`/boq-engine/action`, { action: "pack.get", payload: { country } }),
  // Match rates from the rate library for a BOQ result (used by BoqReviewTab). Same endpoint as bimAiApi.matchRates.
  matchRates: (data: { projectId: number; boqId: number }) =>
    api.post("/ai/rate/match", data),
};

// ============================================================
// BIM Rate Library API
// ============================================================
export const bimRateLibraryApi = {
  listRateItems: (params?: { categoryId?: number; search?: string; discipline?: string }) =>
    api.get("/ratelibrary/items/list", { params }),

  getRateItem: (rateItemId: number) =>
    api.get(`/ratelibrary/item/id/${rateItemId}`),

  createRateItem: (data: any) =>
    api.post("/ratelibrary/item/create", data),

  updateRateItem: (data: any) =>
    api.put("/ratelibrary/item/update", data),

  deleteRateItem: (rateItemId: number) =>
    api.delete(`/ratelibrary/item/delete/${rateItemId}`),

  getCategories: () =>
    api.get("/ratelibrary/categories"),

  createCategory: (data: any) =>
    api.post("/ratelibrary/category/create", data),

  updateCategory: (data: any) =>
    api.put("/ratelibrary/category/update", data),

  deleteCategory: (categoryId: number) =>
    api.delete(`/ratelibrary/category/delete/${categoryId}`),

  getUomMaster: () =>
    api.get("/ratelibrary/uom"),

  getRateDropdown: () =>
    api.get("/ratelibrary/dropdown"),

  importRateLibrary: (data: any) =>
    api.post("/ratelibrary/import", data),

  exportRateLibrary: () =>
    api.post("/ratelibrary/export", {}),
};

// ============================================================
// BIM Linking API
// ============================================================
export const bimLinkingApi = {
  getLinkRules: (params?: { discipline?: string }) =>
    api.get("/linking/rules", { params }),

  createLinkRule: (data: any) =>
    api.post("/linking/rules/create", data),

  updateLinkRule: (data: any) =>
    api.put("/linking/rules/update", data),

  deleteLinkRule: (ruleId: number) =>
    api.delete(`/linking/rules/delete/${ruleId}`),

  triggerAutoLink: (data: { projectId: number; modelId: number }) =>
    api.post("/linking/autoLink", data),

  getLinkedElements: (params: { projectId: number; modelId: number; pageSize?: number }) =>
    api.get(`/linking/linked`, { params }),

  getUnlinkedElements: (params: { projectId: number; modelId: number; pageSize?: number }) =>
    api.get(`/linking/unlinked`, { params }),

  createManualLink: (data: any) =>
    api.post("/linking/manualLink", data),

  getLinkSuggestions: (elementId: number) =>
    api.get(`/linking/suggestions/${elementId}`),

  getLinkingSummary: (modelId: number) =>
    api.get(`/linking/summary/${modelId}`),

  saveClassifications: (data: { modelId: number; projectId: number; classifications: any[] }) =>
    api.post("/linking/classifications/save", data),

  getClassifications: (params: { modelId: number; projectId: number }) =>
    api.get("/linking/classifications", { params }),
};

// ============================================================
// BIM Report API
// ============================================================
export const bimReportApi = {
  listReports: (params: { projectId?: string }) =>
    api.get("/report/list", { params }),

  getReportTemplates: () =>
    api.get("/report/templates"),

  generateReport: (data: { projectId: string; templateId: number; format: string }) =>
    api.post("/report/generate", data),

  downloadReport: (reportId: number) =>
    api.get(`/report/download/${reportId}`, { responseType: "blob" }),
};

// ============================================================
// BIM Collaboration API (Comments)
// ============================================================
export const bimCollabApi = {
  createComment: (data: {
    modelId: number;
    projectId: number;
    elementExpressId?: number;
    ifcClass?: string;
    text: string;
  }) =>
    api.post("/collab/comment/create", {
      projectId: data.projectId,
      entityType: "model",
      entityId: data.modelId,
      content: data.ifcClass
        ? `[${data.ifcClass}${data.elementExpressId ? ` #${data.elementExpressId}` : ""}] ${data.text}`
        : data.text,
    }),

  listComments: (params: { modelId?: number; projectId?: number }) =>
    api.get("/collab/comment/list", {
      params: {
        projectId: params.projectId || 0,
        entityType: "model",
        entityId: params.modelId || 0,
      },
    }),

  updateComment: (data: { commentId: number; text?: string; resolved?: boolean }) =>
    api.put("/collab/comment/update", {
      commentId: data.commentId,
      ...(data.text !== undefined && { content: data.text }),
      ...(data.resolved !== undefined && { resolved: data.resolved ? 1 : 0 }),
    }),

  deleteComment: (commentId: number) =>
    api.delete(`/collab/comment/delete/${commentId}`),

  getAuditTrail: (params?: any) =>
    api.get("/collab/audit/trail", { params }),

  logAudit: (data: {
    projectId: number;
    action: string;
    entityType: string;
    entityId?: number;
    entityName?: string;
    description: string;
    module?: string;
  }) =>
    api.post("/collab/audit/log", data),
};

// ============================================================
// BIM Notification API
// ============================================================
export const bimNotificationApi = {
  listNotifications: () =>
    api.get("/collab/notifications"),

  markNotificationRead: (notificationId: number) =>
    api.put(`/collab/notifications/read/${notificationId}`),

  markAllRead: () =>
    api.put("/collab/notifications/read-all"),
};

// ============================================================
// BIM Master API
// ============================================================
export const bimMasterApi = {
  listMaterials: (params?: { discipline?: string }) =>
    api.get("/master/materials", { params }),

  createMaterial: (data: any) =>
    api.post("/master/materials/create", data),

  updateMaterial: (data: any) =>
    api.put("/master/materials/update", data),

  deleteMaterial: (id: number) =>
    api.delete(`/master/materials/delete/${id}`),

  listIfcClassifications: (params?: { discipline?: string }) =>
    api.get("/master/ifc-classifications", { params }),

  createIfcClassification: (data: any) =>
    api.post("/master/ifc-classifications/create", data),

  updateIfcClassification: (data: any) =>
    api.put("/master/ifc-classifications/update", data),

  deleteIfcClassification: (id: number) =>
    api.delete(`/master/ifc-classifications/delete/${id}`),

  // ---- Layer Classification Rules (client-level POMI layer mapping) ----
  listLayerClassRules: (params?: { pomiSection?: string }) =>
    api.get("/master/layer-class-rules", { params }),

  createLayerClassRule: (data: any) =>
    api.post("/master/layer-class-rules/create", data),

  updateLayerClassRule: (data: any) =>
    api.put("/master/layer-class-rules/update", data),

  deleteLayerClassRule: (id: number) =>
    api.delete(`/master/layer-class-rules/delete/${id}`),

  importLayerClassRules: (data: { items: any[]; replaceExisting?: boolean }) =>
    api.post("/master/layer-class-rules/import", data),

  // ---- Assumed Sections (2D QTO) ----
  listAssumedSections: () =>
    api.get("/master/assumed-sections"),
  createAssumedSection: (data: any) =>
    api.post("/master/assumed-sections/create", data),
  updateAssumedSection: (data: any) =>
    api.put("/master/assumed-sections/update", data),
  deleteAssumedSection: (id: number) =>
    api.delete(`/master/assumed-sections/delete/${id}`),

  // ---- Derived Rules (2D QTO) ----
  listDerivedRules: () =>
    api.get("/master/derived-rules"),
  createDerivedRule: (data: any) =>
    api.post("/master/derived-rules/create", data),
  updateDerivedRule: (data: any) =>
    api.put("/master/derived-rules/update", data),
  deleteDerivedRule: (id: number) =>
    api.delete(`/master/derived-rules/delete/${id}`),

  // ---- Section Profiles (2D QTO) ----
  listSectionProfiles: () =>
    api.get("/master/section-profiles"),
  createSectionProfile: (data: any) =>
    api.post("/master/section-profiles/create", data),
  updateSectionProfile: (data: any) =>
    api.put("/master/section-profiles/update", data),
  deleteSectionProfile: (id: number) =>
    api.delete(`/master/section-profiles/delete/${id}`),
  importSectionProfiles: (data: { items: any[]; replaceExisting?: boolean }) =>
    api.post("/master/section-profiles/import", data),

  listBoqTemplates: () =>
    api.get("/master/boq-templates"),

  listUom: () =>
    api.get("/master/uom"),

  createUom: (data: any) =>
    api.post("/master/uom/create", data),

  updateUom: (data: any) =>
    api.put("/master/uom/update", data),

  deleteUom: (id: number) =>
    api.delete(`/master/uom/delete/${id}`),

  // ---- Global Masters (country/currency/FX/tax/calendar/standard) ----
  listCountries: () => api.get("/master/global/countries"),
  countryCascade: (iso: string, year?: number) =>
    api.get(`/master/global/country/${iso}/cascade`, { params: { year } }),
  listCurrencies: () => api.get("/master/global/currencies"),
  listFxRates: () => api.get("/master/global/fx"),
  listTaxConfigs: (country?: string) =>
    api.get("/master/global/tax", { params: { country } }),
  listWorkCalendars: () => api.get("/master/global/work-calendars"),
  listClassificationStandards: () =>
    api.get("/master/global/classification-standards"),
  saveCountry: (data: any) => api.post("/master/global/country/save", data),
  saveCurrency: (data: any) => api.post("/master/global/currency/save", data),
  saveFxRate: (data: any) => api.post("/master/global/fx/save", data),
  saveTaxConfig: (data: any) => api.post("/master/global/tax/save", data),
  saveWorkCalendar: (data: any) =>
    api.post("/master/global/work-calendar/save", data),
  saveClassificationStandard: (data: any) =>
    api.post("/master/global/classification-standard/save", data),
  deleteGlobal: (entity: string, id: number) =>
    api.delete(`/master/global/${entity}/${id}`),

  // ---- BlackBox: Resource Catalog + Assemblies (org-private) ----
  listResources: (params?: { type?: string; search?: string; currency?: string; page?: number; pageSize?: number }) =>
    api.get("/master/blackbox/resources", { params }),
  // Rate change history for an item (entity: 'rate' | 'resource' | 'assembly').
  rateHistory: (entity: string, id: number) =>
    api.get("/master/blackbox/resources", { params: { historyEntity: entity, historyId: id } }),
  saveResource: (data: any) => api.post("/master/blackbox/resource/save", data),
  listAssemblies: () => api.get("/master/blackbox/assemblies"),
  getAssembly: (id: number) => api.get(`/master/blackbox/assembly/${id}`),
  saveAssembly: (data: any) => api.post("/master/blackbox/assembly/save", data),
  deleteBlackbox: (entity: string, id: number) =>
    api.delete(`/master/blackbox/${entity}/${id}`),
};

// ============================================================
// BIM Speckle Object Streaming API (Azure Blob — replaces MinIO)
// ============================================================
export const bimSpeckleApi = {
  // Stream management
  createStream: (data: { modelId: number; projectId: number; name?: string }) =>
    api.post("/speckle/stream/create", data),

  getStreamByModel: (modelId: number) =>
    api.get(`/speckle/stream/model/${modelId}`),

  // Object streaming
  uploadObjects: (streamId: string, objects: any[], rootObjectId?: string) =>
    api.post(`/speckle/objects/${streamId}`, { objects, rootObjectId }),

  getObjectSingle: (streamId: string, objectId: string) =>
    api.get(`/speckle/objects/${streamId}/${objectId}/single`),

  getObjectWithChildren: (streamId: string, objectId: string) =>
    api.get(`/speckle/objects/${streamId}/${objectId}`, { responseType: "text" }),

  getObjectsBatch: (streamId: string, objectIds: string[]) =>
    api.post(`/speckle/getobjects/${streamId}`, { objects: objectIds }, { responseType: "text" }),

  // Blob storage
  uploadBlob: (streamId: string, fileName: string, fileData: string) =>
    api.post(`/speckle/blob/${streamId}`, { fileName, fileData }),

  getBlobSasUrl: (blobPath: string) =>
    api.post("/speckle/blob/sas-url", { blobPath }),

  // Model conversion
  convertToSpeckle: (modelId: number, projectId: number) =>
    api.post("/speckle/convert", { modelId, projectId }),
};

// ============================================================
// BIM Viewpoints API
// ============================================================
export const bimViewpointsApi = {
  addViewpoint: (issueId: number, data: any) =>
    api.post(`/clash/issues/${issueId}/viewpoints`, data),
  getViewpoints: (issueId: number) =>
    api.get(`/clash/issues/${issueId}/viewpoints`),
  deleteViewpoint: (viewpointId: number) =>
    api.delete(`/clash/issues/viewpoints/${viewpointId}`),
};

// ============================================================
// BIM Watchers & Notifications API
// ============================================================
export const bimWatchersApi = {
  addWatcher: (issueId: number, data: { userId: number; userName?: string; userEmail?: string }) =>
    api.post(`/clash/issues/${issueId}/watchers`, data),
  removeWatcher: (issueId: number, userId: number) =>
    api.delete(`/clash/issues/${issueId}/watchers/${userId}`),
  getWatchers: (issueId: number) =>
    api.get(`/clash/issues/${issueId}/watchers`),
  getNotifications: () =>
    api.get("/clash/notifications"),
  markRead: (notificationId: number) =>
    api.patch(`/clash/notifications/${notificationId}/read`),
};

// ============================================================
// BIM Validation API
// ============================================================
export const bimValidationApi = {
  getRules: (params?: { orgId?: number; category?: string }) =>
    api.get("/clash/validation/rules", { params }),
  createRule: (data: any) =>
    api.post("/clash/validation/rules/create", data),
  updateRule: (data: any) =>
    api.put("/clash/validation/rules/update", data),
  deleteRule: (ruleId: number) =>
    api.delete(`/clash/validation/rules/${ruleId}`),
  runValidation: (data: { projectId: number; modelId: number }) =>
    api.post("/clash/validation/run", data),
  getRuns: (params: { projectId: number; modelId?: number }) =>
    api.get("/clash/validation/runs", { params }),
  getResults: (runId: number) =>
    api.get("/clash/validation/results", { params: { runId } }),
};

// ============================================================
// BIM Shared Views API
// ============================================================
export const bimShareApi = {
  createShare: (data: any) =>
    api.post("/clash/share/create", data),
  getSharedView: (token: string) =>
    api.get(`/clash/share/${token}`),
  listShares: (params: { projectId: number }) =>
    api.get("/clash/share/list", { params }),
  deleteShare: (shareId: number) =>
    api.delete(`/clash/share/${shareId}`),
};

// ============================================================
// BIM RBAC API
// ============================================================
export const bimRbacApi = {
  getRoles: () =>
    api.get("/clash/rbac/roles"),
  createRole: (data: any) =>
    api.post("/clash/rbac/roles/create", data),
  updateRole: (data: any) =>
    api.put("/clash/rbac/roles/update", data),
  deleteRole: (roleId: number) =>
    api.delete(`/clash/rbac/roles/${roleId}`),
  getRolePermissions: (roleId: number) =>
    api.get(`/clash/rbac/roles/${roleId}/permissions`),
  updateRolePermissions: (roleId: number, data: { permissions: string[] }) =>
    api.put(`/clash/rbac/roles/${roleId}/permissions`, data),
  assignUserRole: (data: { userId: number; projectId?: number; roleId: number }) =>
    api.post("/clash/rbac/assign", data),
  removeUserRole: (data: { userId: number; projectId?: number; roleId: number }) =>
    api.delete("/clash/rbac/unassign", { data }),
  getUserRoles: (userId: number) =>
    api.get(`/clash/rbac/user/${userId}/roles`),
  checkPermission: (data: { userId: number; permission: string; projectId?: number }) =>
    api.post("/clash/rbac/check", data),
};

// ============================================================
// BIM Collaboration API (Real-time)
// ============================================================
export const bimCollabSessionApi = {
  createSession: (data: { projectId: number; modelId?: number }) =>
    api.post("/clash/collab/session", data),
  getSession: (token: string) =>
    api.get(`/clash/collab/session/${token}`),
  endSession: (token: string) =>
    api.patch(`/clash/collab/session/${token}/end`),
};

// ============================================================
// BIM AI Agent API
// ============================================================
export const bimAiApi = {
  detectAnomalies: (data: { modelId: number; projectId: number }) =>
    api.post("/ai/anomaly/detect", data),

  checkQuality: (data: { modelId: number; projectId: number }) =>
    api.post("/ai/quality/check", data),

  matchRates: (data: { projectId: number; boqId: number }) =>
    api.post("/ai/rate/match", data),

  chat: (data: { projectId: number; question: string; modelId?: number }) =>
    api.post("/ai/chat", data),

  classifyElements: (data: { modelId: number; projectId: number }) =>
    api.post("/ai/classify", data),

  classifyLayers2d: (data: {
    projectId: number;
    layers: Array<{ layerId: number; layerName: string; entityCount?: number; totalLength?: number; totalArea?: number }>;
  }) => api.post("/ai/classify-layers-2d", data, { timeout: 180000 }), // AI is slow — allow 3 min per batch

  generateWbs: (data: { projectId: number; boqId?: number }) =>
    api.post("/ai/wbs/generate", data),

  suggestResolution: (data: { projectId: number; clashes: any[] }) =>
    api.post("/ai/clash/suggest", data),

  saveConfig: (data: any) =>
    api.post("/ai/config/save", data),

  fetchConfig: () =>
    api.get("/ai/config"),

  // provider "anthropic" tests the AI agents' Claude key; anything else the existing features' provider
  testConnection: (provider?: string) =>
    api.post("/ai/config/test", provider ? { provider } : {}),

  fetchUsageStats: (params?: { days?: number }) =>
    api.get("/ai/usage", { params }),
};

// ============================================================
// AI agents (main agent + sub-agents) - gateway /ai/agents/* -> agent service. The answer stream is in agentsApi.ts.
// ============================================================
export type AgentKey = "classify" | "qto" | "line" | "viewer" | "boq";
const ap = (projectId?: number | null) => (projectId ? { headers: { "X-Project-Id": String(projectId) } } : {});
export const aiAgentsApi = {
  me: (projectId?: number | null) => api.get("/ai/agents/me", { params: projectId ? { projectId } : {}, ...ap(projectId) }),
  chats: (projectId: number, agent?: AgentKey) => api.get("/ai/agents/chats", { params: { projectId, agent }, ...ap(projectId) }),
  /** the AI workspace's popular questions, built from the project's live state and recent activity (no model call) */
  popular: (projectId: number) => api.get("/ai/agents/chats", { params: { projectId, popular: 1 }, ...ap(projectId) }),
  chat: (chatId: number, projectId?: number | null) => api.get(`/ai/agents/chats/${chatId}`, ap(projectId)),
  feedback: (messageId: number, value: 1 | -1, projectId?: number | null, comment?: string) => api.post(`/ai/agents/messages/${messageId}/feedback`, { value, comment }, ap(projectId)),
  proposal: (id: number, projectId?: number | null) => api.get(`/ai/agents/proposals/${id}`, ap(projectId)),
  approve: (id: number, projectId: number, body: { keys?: string[]; overrides?: Record<string, any> } = {}) => api.post(`/ai/agents/proposals/${id}/approve`, { projectId, ...body }, { ...ap(projectId), timeout: 180000 }),
  reject: (id: number, projectId: number, reason?: string) => api.post(`/ai/agents/proposals/${id}/reject`, { projectId, reason }, ap(projectId)),
  explain: (projectId: number, qtoLineId: number) => api.post("/ai/agents/explain", { projectId, qtoLineId }, ap(projectId)),
  settings: () => api.get("/ai/agents/settings"),
  saveSettings: (body: any) => api.put("/ai/agents/settings", body),
  usage: (days = 30) => api.get("/ai/agents/usage", { params: { days } }),
};

// AI take-off check (TAKEOFF_AI_ENGINE_ENABLED) - gateway /ai/agents/takeoff-ai/* -> agent service (AiTakeoffPanel.tsx)
export const takeoffAiApi = {
  status: (projectId: number) => api.get("/ai/agents/takeoff-ai/status", { params: { projectId }, ...ap(projectId) }),
  findings: (projectId: number) => api.get("/ai/agents/takeoff-ai/findings", { params: { projectId }, ...ap(projectId) }),
  run: (projectId: number) => api.post("/ai/agents/takeoff-ai/run", { projectId }, ap(projectId)),
  decide: (projectId: number, body: { findingIds: number[]; action: "approve" | "reject"; reason?: string; overrides?: Record<string, number> }) =>
    api.post("/ai/agents/takeoff-ai/decide", { projectId, ...body }, { ...ap(projectId), timeout: 180000 }),
};

// ============================================================
// BIM Agent AI API — orchestrated classify → QTO → BOQ pipeline
// (separate from the manual 2D/3D flow)
// ============================================================
export const bimAgentApi = {
  // start a run for a project. force=true ignores change-detection;
  // fileIds scopes the run to selected files only.
  startRun: (data: { projectId: number; threshold?: number; force?: boolean; fileIds?: number[] }) =>
    api.post("/agent/run/start", data, { timeout: 300000 }),

  // poll run status / questions
  getRun: (runId: number) =>
    api.get(`/agent/run/${runId}`),

  getLatest: (projectId: number) =>
    api.get("/agent/run", { params: { projectId } }),

  // resolve the classification review queue
  resolveClassifications: (data: { runId: number; answers: any[] }) =>
    api.post("/agent/classifications/resolve", data, { timeout: 120000 }),

  // run QTO (auto-finalize 100%, return needed inputs)
  runQto: (data: { runId: number; projectId: number }) =>
    api.post("/agent/qto/run", data, { timeout: 300000 }),

  // submit assumed-section / derived inputs
  submitInputs: (data: { runId: number; answers: any[] }) =>
    api.post("/agent/qto/inputs", data, { timeout: 120000 }),

  // ask-AI chat grounded in the run
  chat: (data: { runId: number; text: string }) =>
    api.post("/agent/chat", data, { timeout: 120000 }),

  // total AI cost for this project across all runs
  projectCost: (projectId: number) =>
    api.get("/agent/cost", { params: { projectId } }),

  // parse door/window/finish SCHEDULES (text tables) into itemized BOQ lines
  parseSchedules: (projectId: number) =>
    api.post("/agent/schedules/parse", { projectId }, { timeout: 300000 }),

  // parse SECTION/DETAIL drawings for depths/thicknesses → earthworks + real dims
  parseSections: (projectId: number) =>
    api.post("/agent/sections/parse", { projectId }, { timeout: 300000 }),
};

/** Google Drive import (core drive-sync): a client folder shared with the ProcureX service account -> the project's drawings */
export const driveApi = {
  status: (projectId: number, test = false) => boqHttp.post("/api/boq/drive/status", { projectId, test }, boqCtx()),
  browse: (projectId: number, folder?: string | null) => boqHttp.post("/api/boq/drive/browse", { projectId, folder: folder || null }, boqCtx()),
  mappings: (projectId: number) => boqHttp.post("/api/boq/drive/mappings", { projectId }, boqCtx()),
  createMapping: (projectId: number, body: { folder: string; folderPath?: string; options?: { fileTypes?: string[]; defaultBuilding?: string | null; defaultDiscipline?: string | null } }) =>
    boqHttp.post("/api/boq/drive/mappings/create", { projectId, ...body }, boqCtx()),
  deleteMapping: (projectId: number, mappingId: number) => boqHttp.post("/api/boq/drive/mappings/delete", { projectId, mappingId }, boqCtx()),
  syncStart: (projectId: number, mappingId?: number) => boqHttp.post("/api/boq/drive/sync/start", { projectId, mappingId }, boqCtx()),
  retryFailed: (projectId: number, mappingId?: number) => boqHttp.post("/api/boq/drive/sync/retry-failed", { projectId, mappingId }, boqCtx()),
  syncStatus: (projectId: number) => boqHttp.post("/api/boq/drive/sync/status", { projectId }, boqCtx()),
  files: (projectId: number, body: { status?: string; mappingId?: number; search?: string } = {}) => boqHttp.post("/api/boq/drive/files", { projectId, ...body }, boqCtx()),
  links: (projectId: number) => boqHttp.post("/api/boq/drive/links", { projectId }, boqCtx()),
};
